import { Auth } from 'aws-amplify';
import appConfig from '../config/app-config';

const API = appConfig.apis.contractApiEnv;
const baseUrl = API.address;

export class ApiError extends Error {
    constructor(public status: number, message: string) {
        super(message);
    }
}

const getHeaders = async (): Promise<Record<string, string>> => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'Content-Type': 'application/json',
        'x-api-key': API.key,
        'Authorization': `Bearer ${token}`,
    };
};

const handleResponse = async (res: Response) => {
    if (res.ok) {
        if (res.status === 204 || res.headers.get('content-length') === '0') return {};
        const text = await res.text();
        if (!text) return {};
        const json = JSON.parse(text);
        return json.data !== undefined ? json.data : json;
    }
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.errors?.[0]?.message || body.message || `API error: ${res.status}`);
};

const post = async (path: string, data: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { method: 'POST', headers: await getHeaders(), body: JSON.stringify(data) }));

const put = async (path: string, data: Record<string, unknown>) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { method: 'PUT', headers: await getHeaders(), body: JSON.stringify(data) }));

const get = async (path: string) =>
    handleResponse(await fetch(`${baseUrl}${path}`, { headers: await getHeaders() }));

// --- Contract Type Configuration Types ---

export interface RoleRequirementDto {
    role: string;
    label: string;
    minCount: number;
    required: boolean;
    participantSource: 'RECIPIENT' | 'ASSIGNOR' | 'ANY';
    signingOrder: number;
}

export interface ContractStructureDto {
    name: string;
    roles: RoleRequirementDto[];
}

export interface ContractTypeDto {
    name: string;
    label: string;
    active: boolean;
    structures: ContractStructureDto[];
    defaultObservers?: { personUuid?: string; name: string; email: string }[];
    allowedProducts?: string[];
}

export type ContractTypeChangeType = 'STRUCTURES' | 'DEFAULT_OBSERVERS' | 'ALLOWED_PRODUCTS';

export interface ContractTypeAuditEntry {
    id: number;
    contractTypeName: string;
    userId: string;
    changeType?: ContractTypeChangeType;
    previousStructures: unknown;
    newStructures: unknown;
    changedAt: string;
}

export const contractApi = {
    // Contract Types Configuration
    getContractTypes: (): Promise<ContractTypeDto[]> =>
        get('/contract-types'),

    getContractType: (name: string): Promise<ContractTypeDto> =>
        get(`/contract-types/${name}`),

    updateContractTypeStructures: (name: string, structures: ContractStructureDto[]): Promise<void> =>
        put(`/contract-types/${name}/structures`, { structures } as Record<string, unknown>),

    createContractType: (name: string, structures: ContractStructureDto[]): Promise<void> =>
        post('/contract-types', { name, structures } as Record<string, unknown>),

    getContractTypeAudit: (name: string): Promise<ContractTypeAuditEntry[]> =>
        get(`/contract-types/${name}/audit`),

    updateContractTypeDefaultObservers: (name: string, defaultObservers: { personUuid?: string; name: string; email: string }[]) =>
        put(`/contract-types/${name}/default-observers`, { defaultObservers } as Record<string, unknown>),

    updateContractTypeAllowedProducts: (name: string, allowedProducts: string[]) =>
        put(`/contract-types/${name}/allowed-products`, { allowedProducts } as Record<string, unknown>),

    // Documents
    createDocument: (fileName: string, file: string, contractType?: string, externalId?: string) =>
        post('/documents', { fileName, file, contractType, externalId }),

    getDocumentPageForm: (documentId: string) =>
        get(`/documents/${documentId}/page-form`),

    // Extractions
    createExtraction: (documentId: string, contractId: string, feedbacks?: { field: string; extractedValue: string }[]) =>
        post('/extractions', { documentId, contractId, feedbacks }),

    getExtractionPageForm: (extractionId: string) =>
        get(`/extractions/${extractionId}/page-form`),

    // Drafts
    getDraft: (extractionId: string) =>
        get(`/drafts?extractionId=${extractionId}`),

    // Contracts
    getContractPageForm: (contractId: string) =>
        get(`/contracts/${contractId}/page-form`),

    updateContract: (contractId: string, data: { contractType?: string; signers?: Record<string, unknown>[]; metaData?: Record<string, unknown> }) =>
        put(`/contracts/${contractId}`, data),

    sendToSignature: (contractId: string, providerId: string, deliveryChannels?: { whatsapp?: boolean; sms?: boolean }, reminderConfig?: { reminderDelay?: number; reminderFrequency?: number; expireAfter?: number }, observers?: { name: string; email: string }[]) =>
        post(`/contracts/${contractId}/send-to-signature`, { providerId, deliveryChannels, reminderConfig, observers }),

    releaseForResend: (contractId: string) =>
        post(`/contracts/${contractId}/release-for-resend`, {}),

    validateForSignature: (contractId: string, data: { signers: Record<string, unknown>[]; operationData: Record<string, unknown>; extractedData: Record<string, unknown>; contractType: string }) =>
        post(`/contracts/${contractId}/validate-for-signature`, data),

    getList: (params?: { page?: number; size?: number }) =>
        get(`/contracts/page-list?page=${params?.page || 1}&size=${params?.size || 100}`),
};
